import * as THREE from "three";

import type { AudioFeatures } from "../audio/types";
import type { AltSceneApi, CameraPersonality } from "./altScenes";
import type { BioclastParams } from "./scenes/bioclast";

export type ChurchProjectionMode =
  | "sanctuaryLasers"
  | "stainedGlassAwakening"
  | "cathedralOrgan"
  | "celestialAscension";

const MODE_ID: Record<ChurchProjectionMode, number> = {
  sanctuaryLasers: 0,
  stainedGlassAwakening: 1,
  cathedralOrgan: 2,
  celestialAscension: 3,
};

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  varying vec2 vUv;

  uniform float uTime;
  uniform float uBass;
  uniform float uMid;
  uniform float uTreble;
  uniform float uBeatEnv;
  uniform float uBeatPhase;
  uniform float uBeatCount;
  uniform float uMode;
  uniform float uHueShift;
  uniform float uSaturation;
  uniform float uBrightness;
  uniform float uDrive;

  vec3 hsv2rgb(vec3 c) {
    vec3 p = abs(fract(c.xxx + vec3(1.0, 2.0 / 3.0, 1.0 / 3.0)) * 6.0 - 3.0);
    return c.z * mix(vec3(1.0), clamp(p - 1.0, 0.0, 1.0), c.y);
  }

  vec2 hash22(vec2 p) {
    return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453);
  }

  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash22(i).x;
    float b = hash22(i + vec2(1.0, 0.0)).x;
    float c = hash22(i + vec2(0.0, 1.0)).x;
    float d = hash22(i + vec2(1.0, 1.0)).x;
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  float sdSegment(vec2 p, vec2 a, vec2 b) {
    vec2 pa = p - a;
    vec2 ba = b - a;
    float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
    return length(pa - ba * h);
  }

  float sdGothicArch(vec2 p, float cx, float yBot, float ySpring, float yApex, float hw) {
    float dx = abs(p.x - cx);
    if (p.y < yBot) {
      return length(vec2(max(dx - hw, 0.0), yBot - p.y));
    }
    if (p.y <= ySpring) {
      return max(dx - hw, yBot - p.y);
    }
    float h = max(yApex - ySpring, 0.001);
    float t = (p.y - ySpring) / h;
    if (t <= 1.0) {
      float ang = t * 1.5707963;
      float w = hw * cos(ang);
      float slope = (hw * 1.5707963 / h) * sin(ang);
      return (dx - w) / sqrt(1.0 + slope * slope);
    }
    return length(vec2(dx, p.y - yApex));
  }

  vec2 spireField(vec2 p, float cx, float yBase, float yTip, float baseHw) {
    float dx = abs(p.x - cx);
    float roofEdge = sdSegment(vec2(dx, p.y), vec2(baseHw, yBase), vec2(0.0, yTip));
    float eavesEdge = sdSegment(vec2(dx, p.y), vec2(0.0, yBase), vec2(baseHw, yBase));
    float dEdge = min(roofEdge, eavesEdge);
    float t = clamp((p.y - yBase) / max(yTip - yBase, 0.001), 0.0, 1.0);
    float fill = step(yBase, p.y) * step(p.y, yTip) * smoothstep(baseHw * (1.0 - t) + 0.002, baseHw * (1.0 - t) - 0.002, dx);
    return vec2(dEdge, fill);
  }

  vec4 churchWindows(vec2 p) {
    vec2 sp = vec2(0.5 - abs(p.x - 0.5), p.y);
    float dUpper = sdGothicArch(p, 0.500, 0.602, 0.680, 0.760, 0.055);
    float dMidC  = sdGothicArch(p, 0.500, 0.432, 0.498, 0.564, 0.056);
    float dMidL  = sdGothicArch(p, 0.369, 0.432, 0.495, 0.558, 0.032);
    float dMidR  = sdGothicArch(p, 0.631, 0.432, 0.495, 0.558, 0.032);
    float dSideOut = sdGothicArch(sp, 0.051, 0.115, 0.305, 0.345, 0.011);
    float dSideMid = sdGothicArch(sp, 0.198, 0.115, 0.365, 0.415, 0.013);

    float dWin = min(min(min(dUpper, dMidC), min(dMidL, dMidR)), min(dSideOut, dSideMid));
    float winId = dUpper < dMidC && dUpper < dMidL && dUpper < dMidR ? 3.0
      : (dMidC < dMidL && dMidC < dMidR ? 2.0 : (dMidL < dMidR ? 1.0 : 4.0));

    float dSub1 = sdGothicArch(p, 0.474, 0.602, 0.655, 0.695, 0.022);
    float dSub2 = sdGothicArch(p, 0.526, 0.602, 0.655, 0.695, 0.022);
    float dSub3 = sdGothicArch(p, 0.473, 0.432, 0.482, 0.512, 0.023);
    float dSub4 = sdGothicArch(p, 0.527, 0.432, 0.482, 0.512, 0.023);
    float dSub5 = sdGothicArch(p, 0.353, 0.432, 0.480, 0.510, 0.013);
    float dSub6 = sdGothicArch(p, 0.385, 0.432, 0.480, 0.510, 0.013);
    float dSub7 = sdGothicArch(p, 0.615, 0.432, 0.480, 0.510, 0.013);
    float dSub8 = sdGothicArch(p, 0.647, 0.432, 0.480, 0.510, 0.013);
    float dSub = min(min(min(dSub1, dSub2), min(dSub3, dSub4)), min(min(dSub5, dSub6), min(dSub7, dSub8)));

    float dOcU = abs(length((p - vec2(0.500, 0.718)) * vec2(1.0, 1.35)) - 0.021);
    float dOcC = abs(length((p - vec2(0.500, 0.530)) * vec2(1.0, 1.35)) - 0.022);
    float dOcL = abs(length((p - vec2(0.369, 0.526)) * vec2(1.0, 1.35)) - 0.013);
    float dOcR = abs(length((p - vec2(0.631, 0.526)) * vec2(1.0, 1.35)) - 0.013);
    float dOcRing = min(min(dOcU, dOcC), min(dOcL, dOcR));

    float fillOc = smoothstep(0.023, 0.018, min(
      min(length((p - vec2(0.500, 0.718)) * vec2(1.0, 1.35)), length((p - vec2(0.500, 0.530)) * vec2(1.0, 1.35))),
      min(length((p - vec2(0.369, 0.526)) * vec2(1.0, 1.35)) * 1.6, length((p - vec2(0.631, 0.526)) * vec2(1.0, 1.35)) * 1.6)
    ));

    float fill = smoothstep(0.003, -0.003, dWin);
    float rib = max(
      smoothstep(0.0055, 0.001, abs(dWin)),
      fill * max(smoothstep(0.004, 0.0008, abs(dSub)), smoothstep(0.004, 0.0008, dOcRing))
    );
    return vec4(fill, rib, fillOc, winId);
  }

  vec4 churchStructure(vec2 p) {
    vec2 sp = vec2(0.5 - abs(p.x - 0.5), p.y);

    float a0 = abs(sdGothicArch(p, 0.500, 0.00, 0.145, 0.242, 0.064));
    float a1 = abs(sdGothicArch(p, 0.500, 0.00, 0.150, 0.276, 0.095));
    float a2 = abs(sdGothicArch(p, 0.500, 0.00, 0.155, 0.312, 0.126));
    float a3 = abs(sdGothicArch(p, 0.500, 0.00, 0.160, 0.355, 0.158));
    float wimberg = sdSegment(sp, vec2(0.323, 0.215), vec2(0.500, 0.398));
    float portalRib = smoothstep(0.0055, 0.001, min(min(a0, a1), min(min(a2, a3), wimberg)));

    float bOuter = min(sdSegment(sp, vec2(0.022, 0.00), vec2(0.022, 0.419)), sdSegment(sp, vec2(0.080, 0.00), vec2(0.080, 0.419)));
    float bMid   = min(sdSegment(sp, vec2(0.168, 0.00), vec2(0.168, 0.495)), sdSegment(sp, vec2(0.228, 0.00), vec2(0.228, 0.495)));
    float bInner = min(sdSegment(sp, vec2(0.296, 0.00), vec2(0.296, 0.670)), sdSegment(sp, vec2(0.350, 0.00), vec2(0.350, 0.670)));
    float bBelf  = sdSegment(sp, vec2(0.421, 0.405), vec2(0.421, 0.782));
    float c0 = sdSegment(sp, vec2(0.022, 0.045), vec2(0.350, 0.045));
    float c1 = sdSegment(sp, vec2(0.022, 0.405), vec2(0.500, 0.405));
    float c2 = sdSegment(sp, vec2(0.296, 0.588), vec2(0.500, 0.588));
    float c3 = sdSegment(sp, vec2(0.410, 0.782), vec2(0.500, 0.782));
    float gableMain = sdSegment(sp, vec2(0.323, 0.600), vec2(0.500, 0.780));
    float gableSide = sdSegment(sp, vec2(0.198, 0.420), vec2(0.323, 0.518));
    float buttressRib = smoothstep(
      0.0055,
      0.001,
      min(min(min(bOuter, bMid), min(bInner, bBelf)), min(min(min(c0, c1), min(c2, c3)), min(gableMain, gableSide)))
    );

    vec2 sOut  = spireField(sp, 0.051, 0.419, 0.579, 0.036);
    vec2 sMid  = spireField(sp, 0.198, 0.495, 0.660, 0.036);
    vec2 sInn  = spireField(sp, 0.323, 0.670, 0.826, 0.035);
    vec2 sPin  = spireField(sp, 0.421, 0.782, 0.854, 0.018);
    vec2 sCent = spireField(p,  0.500, 0.782, 0.990, 0.068);
    float dSpireEdge = min(min(sOut.x, sMid.x), min(min(sInn.x, sPin.x), sCent.x));
    float spireFill = max(max(sOut.y, sMid.y), max(max(sInn.y, sPin.y), sCent.y));
    float shingleGrid = spireFill * (0.35 + 0.35 * sin(p.y * 190.0) * cos((p.x - 0.5) * 240.0));
    float spireRib = max(smoothstep(0.0055, 0.001, dSpireEdge), shingleGrid * 0.55);

    float f1 = length((sp - vec2(0.051, 0.575)) * vec2(1.0, 1.4));
    float f2 = length((sp - vec2(0.198, 0.656)) * vec2(1.0, 1.4));
    float f3 = length((sp - vec2(0.323, 0.822)) * vec2(1.0, 1.4));
    float f4 = length((sp - vec2(0.421, 0.852)) * vec2(1.0, 1.4));
    float f5 = length((p  - vec2(0.500, 0.968)) * vec2(1.0, 1.4));
    float dFinial = min(min(f1, f2), min(min(f3, f4), f5));
    float finialGlow = exp(-dFinial * 95.0) + smoothstep(0.014, 0.003, dFinial);

    return vec4(portalRib, buttressRib, spireRib, finialGlow);
  }

  vec3 stainedGlassAt(vec2 p, float winId, float t, float bassBoost, float trebBoost) {
    vec2 center = winId > 2.5 && winId < 3.5 ? vec2(0.500, 0.690)
      : (winId > 1.5 && winId < 2.5 ? vec2(0.500, 0.505)
      : (winId < 1.5 ? vec2(0.369, 0.500) : vec2(0.631, 0.500)));
    vec2 rel = (p - center) * vec2(26.0, 34.0);
    float r = length(rel);
    float a = atan(rel.y, rel.x) + t * 0.22 * (mod(winId, 2.0) * 2.0 - 1.0);
    float seg = 6.2831853 / 12.0;
    float fa = abs(mod(a, seg) - seg * 0.5);
    vec2 g = vec2(cos(fa), sin(fa)) * r * 3.2;
    vec2 cell = floor(g);
    float d1 = 9.0;
    float d2 = 9.0;
    vec2 cid = vec2(0.0);
    for (int y = -1; y <= 1; y++) {
      for (int x = -1; x <= 1; x++) {
        vec2 c = cell + vec2(float(x), float(y));
        vec2 pt = c + 0.18 + 0.64 * hash22(c + winId * 7.1);
        float d = length(g - pt);
        if (d < d1) { d2 = d1; d1 = d; cid = c; } else if (d < d2) { d2 = d; }
      }
    }
    float lead = smoothstep(0.035, 0.095, d2 - d1);
    float hue = fract(hash22(cid).x * 0.65 + winId * 0.14 + t * 0.05 + uHueShift);
    vec3 jewel = hsv2rgb(vec3(hue, clamp(0.88 * uSaturation, 0.0, 1.0), 0.95 + 0.45 * bassBoost));
    jewel *= (0.65 + 0.45 * (1.0 - d1)) * lead;
    jewel += vec3(1.0, 0.86, 0.55) * (1.0 - lead) * 0.18 * trebBoost;
    return jewel;
  }

  void main() {
    vec2 uv = vUv;
    vec4 win = churchWindows(uv);
    vec4 arch = churchStructure(uv);
    float bass = (0.85 + 1.35 * uBass + 0.65 * uBeatEnv) * uDrive;
    float mid = (0.85 + 1.25 * uMid + 0.35 * uBeatEnv) * uDrive;
    float treb = (0.85 + 1.25 * uTreble) * uDrive;

    vec3 col = vec3(0.015, 0.012, 0.022);

    if (uMode < 0.5) {
      // Mode 0: Sanctuary Lasers
      float pulseWave = pow(0.5 + 0.5 * sin(uv.y * 26.0 - uTime * 8.5 - uBeatPhase * 6.28318), 5.0);
      float archPulse = pow(0.5 + 0.5 * cos(length((uv - vec2(0.5, 0.14)) * vec2(1.0, 1.35)) * 34.0 - uTime * 6.0 - uBeatCount * 1.57), 4.0);
      vec3 cyanLaser = hsv2rgb(vec3(fract(0.52 + uHueShift + 0.08 * sin(uTime * 0.25)), clamp(0.85 * uSaturation, 0.0, 1.0), 1.0));
      vec3 magentaLaser = hsv2rgb(vec3(fract(0.86 + uHueShift + 0.06 * cos(uTime * 0.31)), clamp(0.88 * uSaturation, 0.0, 1.0), 1.0));
      vec3 goldLaser = hsv2rgb(vec3(fract(0.11 + uHueShift), clamp(0.75 * uSaturation, 0.0, 1.0), 1.0));

      col += arch.x * mix(magentaLaser, goldLaser, archPulse) * (0.85 + 1.25 * bass) * (0.45 + 0.55 * archPulse);
      col += arch.y * mix(cyanLaser, magentaLaser, uv.y) * (0.65 + 1.15 * pulseWave * mid);
      float spireScan = 0.5 + 0.5 * sin(uv.y * 120.0 - uTime * 12.0);
      col += arch.z * mix(cyanLaser, goldLaser, spireScan) * (0.8 + 1.15 * treb);
      float oculusSpin = 0.5 + 0.5 * sin(atan(uv.y - 0.53, uv.x - 0.5) * 12.0 + uTime * 4.0);
      col += win.y * mix(goldLaser, cyanLaser, oculusSpin) * (0.85 + 0.9 * mid);
      col += win.z * magentaLaser * (0.5 + 0.9 * oculusSpin * bass);
      col += arch.w * goldLaser * (0.9 + 1.6 * uBeatEnv * uDrive);
    } else if (uMode < 1.5) {
      // Mode 1: Stained Glass Awakening
      float n = vnoise(uv * vec2(14.0, 20.0) + vec2(0.0, -uTime * 0.35));
      float vein = pow(max(0.0, 1.0 - abs(n - 0.5) * 8.5), 3.0);
      vec3 stoneCol = vec3(0.045, 0.032, 0.024) + vec3(0.95, 0.62, 0.22) * vein * (0.3 + 0.7 * bass);
      vec3 glassCol = stainedGlassAt(uv, win.w, uTime, uBass + uBeatEnv * 0.6, treb);

      float dDoor = sdGothicArch(uv, 0.500, 0.00, 0.145, 0.242, 0.064);
      float doorFill = smoothstep(0.004, -0.004, dDoor);
      float portalWave = 0.6 + 0.4 * sin(length((uv - vec2(0.5, 0.10)) * vec2(1.2, 1.0)) * 40.0 - uTime * 5.0);
      vec3 portalCol = mix(vec3(0.95, 0.42, 0.12), vec3(1.0, 0.85, 0.38), portalWave) * (0.65 + 0.65 * bass);

      col = mix(stoneCol, glassCol, win.x);
      col = mix(col, portalCol, doorFill * 0.85);
      vec3 ribGlow = mix(vec3(1.0, 0.76, 0.34), vec3(0.35, 0.88, 1.0), 0.5 + 0.5 * sin(uv.y * 10.0 - uTime * 2.2));
      col += (win.y * 0.95 + arch.x * 1.15 + arch.y * 0.75 + arch.z * 0.95) * ribGlow * (0.65 + 0.75 * mid);
      col += arch.w * vec3(1.0, 0.9, 0.55) * (0.8 + 1.4 * treb);
    } else if (uMode < 2.5) {
      // Mode 2: Cathedral Organ
      vec2 sp = vec2(0.5 - abs(uv.x - 0.5), uv.y);
      float colCent = exp(-pow((uv.x - 0.500) / 0.055, 2.0)) * step(uv.y, 0.98);
      float colInn  = exp(-pow((sp.x - 0.323) / 0.032, 2.0)) * step(uv.y, 0.826);
      float colMid  = exp(-pow((sp.x - 0.198) / 0.032, 2.0)) * step(uv.y, 0.660);
      float colOut  = exp(-pow((sp.x - 0.051) / 0.028, 2.0)) * step(uv.y, 0.579);

      float hCent = clamp(0.28 + 0.48 * bass, 0.1, 0.98);
      float hInn  = clamp(0.24 + 0.42 * mid, 0.1, 0.826);
      float hMid  = clamp(0.20 + 0.36 * (0.5 * mid + 0.5 * treb), 0.1, 0.660);
      float hOut  = clamp(0.18 + 0.34 * treb, 0.1, 0.579);

      float segs = 0.65 + 0.35 * sin(uv.y * 95.0 - uTime * 6.0);
      vec3 organCol = vec3(0.0);
      organCol += colCent * smoothstep(hCent + 0.06, hCent - 0.04, uv.y) * vec3(1.0, 0.52, 0.16) * segs;
      organCol += colInn  * smoothstep(hInn  + 0.05, hInn  - 0.04, uv.y) * vec3(0.95, 0.24, 0.68) * segs;
      organCol += colMid  * smoothstep(hMid  + 0.05, hMid  - 0.04, uv.y) * vec3(0.22, 0.82, 1.00) * segs;
      organCol += colOut  * smoothstep(hOut  + 0.05, hOut  - 0.04, uv.y) * vec3(0.35, 1.00, 0.68) * segs;

      float cym = sin((uv.x - 0.5) * 85.0) * cos(uv.y * 85.0 - uTime * 4.5)
                + cos((uv.x - 0.5) * 55.0 + uTime * 3.2) * sin(uv.y * 110.0);
      vec3 cymCol = hsv2rgb(vec3(fract(0.08 + uHueShift + 0.15 * cym + uTime * 0.04), clamp(0.82 * uSaturation, 0.0, 1.0), 0.95)) * (0.5 + 0.5 * abs(cym));

      float w1 = 0.405 + 0.012 * bass * sin(uv.x * 28.0 + uTime * 7.0);
      float w2 = 0.588 + 0.010 * mid * sin(uv.x * 42.0 - uTime * 8.5);
      float w3 = 0.782 + 0.008 * treb * cos(uv.x * 54.0 + uTime * 10.0);
      float ribbon = smoothstep(0.006, 0.001, min(abs(uv.y - w1), min(abs(uv.y - w2), abs(uv.y - w3))));

      col += organCol * 1.15;
      col = mix(col, cymCol * (0.8 + 0.5 * mid), win.x * 0.82);
      col += ribbon * vec3(1.0, 0.92, 0.62) * (0.7 + 0.6 * bass);
      col += (arch.x + arch.y + arch.z + win.y) * vec3(0.95, 0.78, 0.42) * 0.65;
      col += arch.w * vec3(1.0, 0.95, 0.75) * (0.8 + 1.3 * bass);
    } else {
      // Mode 3: Celestial Ascension
      vec2 pRel = uv - vec2(0.500, 0.14);
      float gothicDist = length(vec2(abs(pRel.x) * (1.35 + 0.55 * max(0.0, pRel.y)), pRel.y * 0.92));
      float shock = pow(0.5 + 0.5 * cos(gothicDist * 32.0 - uTime * 5.5 - uBeatPhase * 6.28318), 6.0);

      float n1 = vnoise(vec2(uv.x * 18.0, uv.y * 12.0 - uTime * 1.4));
      float n2 = vnoise(vec2(uv.x * 36.0 - 3.1, uv.y * 24.0 - uTime * 2.3));
      float embers = smoothstep(0.70, 0.95, n1 * 0.55 + n2 * 0.45);

      vec2 wRel = (uv - vec2(0.500, 0.530)) * vec2(1.0, 1.38);
      float astrolabe = abs(sin(atan(wRel.y, wRel.x) * 8.0 + uTime * 2.5) * cos(length(wRel) * 65.0 - uTime * 4.0));

      vec3 deepGold = hsv2rgb(vec3(fract(0.11 + uHueShift), clamp(0.78 * uSaturation, 0.0, 1.0), 1.0));
      vec3 celestialBlue = hsv2rgb(vec3(fract(0.57 + uHueShift), clamp(0.78 * uSaturation, 0.0, 1.0), 1.0));
      vec3 roseViolet = hsv2rgb(vec3(fract(0.86 + uHueShift), clamp(0.75 * uSaturation, 0.0, 1.0), 0.95));

      col += mix(celestialBlue, deepGold, shock) * shock * (0.35 + 0.85 * (arch.x + arch.y + arch.z + win.y)) * (0.7 + 0.7 * bass);
      col += embers * mix(deepGold, roseViolet, uv.y) * (0.45 + 0.85 * treb);
      col += win.x * mix(celestialBlue, deepGold, astrolabe) * (0.55 + 0.6 * mid);
      col += (arch.x * 1.1 + arch.y * 0.75 + arch.z * 0.95 + win.y * 0.9) * mix(deepGold, celestialBlue, 0.5 + 0.5 * sin(uv.y * 8.0 - uTime));
      col += arch.w * vec3(1.0, 0.94, 0.72) * (1.0 + 1.5 * uBeatEnv);
    }

    gl_FragColor = vec4(col * uBrightness, 1.0);
  }
`;

export class ChurchProjectionScene implements AltSceneApi {
  scene = new THREE.Scene();
  camera: THREE.PerspectiveCamera;
  readonly backdrop = { r: 8, g: 6, b: 14, alpha: 0.25, blurPx: 24 };
  readonly cameraPersonality: CameraPersonality = "staticParallax";
  beatLevel = 0;

  private mode: ChurchProjectionMode;
  private quad: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private elapsed = 0;
  private visualTime: number | null = null;
  private beatPhase = 0;

  private baseHue: number;

  constructor(w: number, h: number, mode: ChurchProjectionMode) {
    this.mode = mode;
    this.baseHue = mode === "sanctuaryLasers" ? 192
      : mode === "stainedGlassAwakening" ? 38
      : mode === "cathedralOrgan" ? 28
      : 220;
    this.camera = new THREE.PerspectiveCamera(50, Math.max(0.1, w / Math.max(1, h)), 0.1, 100);
    this.camera.position.set(0, 0, 2.2);
    this.camera.lookAt(0, 0, 0);

    const geom = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uTime: { value: 0 },
        uBass: { value: 0 },
        uMid: { value: 0 },
        uTreble: { value: 0 },
        uBeatEnv: { value: 0 },
        uBeatPhase: { value: 0 },
        uBeatCount: { value: 0 },
        uMode: { value: MODE_ID[mode] },
        uHueShift: { value: 0 },
        uSaturation: { value: 1 },
        uBrightness: { value: 1 },
        uDrive: { value: 1 },
      },
    });
    this.quad = new THREE.Mesh(geom, mat);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  setSize(w: number, h: number): void {
    this.camera.aspect = Math.max(0.1, w / Math.max(1, h));
    this.camera.updateProjectionMatrix();
  }

  setVisualTime(timeSeconds: number | null): void {
    this.visualTime = timeSeconds;
  }

  update(dt: number, params: BioclastParams, features: AudioFeatures): void {
    const speed = Math.max(0.1, params.flowSpeed ?? 1);
    this.elapsed += dt * speed;
    const t = this.visualTime !== null ? this.visualTime * speed : this.elapsed;

    if (features.beat > this.beatLevel) {
      this.beatLevel = features.beat;
    } else {
      this.beatLevel *= Math.exp(-dt * 5.5);
    }

    const bpm = features.bpm > 30 ? features.bpm : 120;
    this.beatPhase = (this.beatPhase + dt * (bpm / 60)) % 1;

    const u = this.quad.material.uniforms;
    u.uTime.value = t;
    u.uBass.value = features.bass;
    u.uMid.value = features.mid;
    u.uTreble.value = features.treble;
    u.uBeatEnv.value = this.beatLevel * (0.6 + 0.4 * (params.bassGravity ?? 1.4));
    u.uBeatPhase.value = this.beatPhase;
    u.uBeatCount.value = features.beatCount ?? 0;
    u.uMode.value = MODE_ID[this.mode];
    const hueDelta = ((params.paletteHue ?? this.baseHue) - this.baseHue) / 360;
    u.uHueShift.value = ((hueDelta % 1) + 1) % 1;
    u.uSaturation.value = params.saturation ?? 1;
    u.uBrightness.value = (params.intensity ?? 1.45) / 1.45;
    u.uDrive.value = 0.55 + 0.35 * (params.cymaticStrength ?? 1.4);
  }

  getDiagnostics() {
    return {
      backend: "church-projection-shader",
      particleCount: 0,
      look: this.mode,
    };
  }

  dispose(): void {
    this.quad.geometry.dispose();
    this.quad.material.dispose();
    this.scene.clear();
  }
}
